import React, { useEffect } from 'react';
import { AiSpinner, Flex, Stack, Text, TextLink, } from '@stepstone-genesis/components';
// import { getLang } from 'client/utils/cookies';
// import { useEscapeKey } from 'client/components/AskMeAnything/hooks/useEscapeKey';
import Input from 'modules/hub/components/AMAChat/AskMeAnything/Input/Input';
import labels from 'modules/hub/components/AMAChat/constants/labels/ama';
const Sidebar = ({ inputDisabled, isLoadingHistory, lang, children, onSendMessage, onInputChange, amaConversationID, companyId, messageCount, hideHeader, isOpen = true, }) => {
    const chatTitle = labels[lang].chatTitle;
    const stepstoneAiBadge = labels[lang].stepstoneAiBadge;
    const chatStorageNotice = labels[lang].chatStorageNotice;
    const chatStorageNoticeLinkText = labels[lang].chatStorageNoticeLinkText;
    const chatStorageNoticeLinkUrl = labels[lang].chatStorageNoticeLinkUrl;
    const reportProblemLinkText = labels[lang].reportProblemLinkText;
    const reportProblemLinkUrl = labels[lang].reportProblemLinkUrl.replace('{companyId}', companyId ?? '');
    const inputRef = React.useRef(null);
    const scrollContainerRef = React.useRef(null);
    useEffect(() => {
        const el = inputRef.current;
        if (el)
            el.focus();
    }, []);
    useEffect(() => {
        if (!inputDisabled) {
            const el = inputRef.current;
            if (el)
                el.focus();
        }
    }, [inputDisabled]);
    // Instant scroll to bottom when history finishes loading.
    // requestAnimationFrame defers the measurement until after the browser has
    // painted the new content, so scrollHeight reflects the actual laid-out height.
    useEffect(() => {
        if (isLoadingHistory)
            return;
        const raf = requestAnimationFrame(() => {
            if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTo({
                    top: scrollContainerRef.current.scrollHeight,
                    behavior: 'instant',
                });
            }
        });
        return () => cancelAnimationFrame(raf);
    }, [isLoadingHistory]);
    // Smooth scroll to bottom when new messages arrive
    useEffect(() => {
        if (isLoadingHistory)
            return;
        const raf = requestAnimationFrame(() => {
            if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTo({
                    top: scrollContainerRef.current.scrollHeight,
                    behavior: 'smooth',
                });
            }
        });
        return () => cancelAnimationFrame(raf);
    }, [children]);
    const handleSendMessage = (message) => {
        onSendMessage(message);
    };
    return (React.createElement(Flex, { flxDirection: "column", "data-atx-component": "askMeAnything", "data-atx-onvisibleelement-payload": JSON.stringify({
            linkName: 'componentVisible',
            componentId: 'askMeAnything',
            componentState: 'noValue',
            elementActioned: 'component',
            elementAction: 'visible',
            elementActionValue: 'noValue',
            amaConversationID: 'noValue',
            companyId: companyId || 'noValue',
        }), sx: {
            flex: 1,
            minHeight: 0,
            // Opaque background so WordPress content underneath the mobile sheet
            // does not bleed through. Note: `colorBackgroundDefault` does not exist
            // in the coretwentyfive theme — use the `…PrimaryDefault` variant.
            backgroundColor: 'colorBackgroundPrimaryDefault',
        } },
        !hideHeader && (React.createElement(Flex, { flxDirection: "column", pl: "spaceM", sx: {
                flexShrink: 0,
            } },
            React.createElement(Stack, { direction: "vertical", spacing: "spaceS", padding: "spaceM", pt: "spaceM", sx: {
                    alignItems: 'center',
                    width: '100%',
                } },
                React.createElement(Text, { fontWeight: "fontWeightBold", size: "medium", truncate: true }, chatTitle),
                React.createElement(Stack, { direction: "horizontal", flxItems: "center", spacing: "spaceXs" },
                    React.createElement(AiSpinner, null),
                    React.createElement(Text, { fontWeight: "fontWeightMedium", fontSize: "fontSizeXxs" }, stepstoneAiBadge))))),
        React.createElement(Flex, { ref: scrollContainerRef, flxDirection: "column", sx: {
                display: isOpen ? 'flex' : 'none',
                overflowY: 'auto',
                overflowX: 'hidden',
                // Prevent the chat scroll from chaining into the page body once
                // the user reaches the top/bottom of the conversation on iOS.
                overscrollBehavior: 'contain',
                padding: 'spaceM',
                flexGrow: 1,
                mb: 'spaceM',
                minHeight: 0,
                scrollbarWidth: 'none', // Firefox
                '&::-webkit-scrollbar': {
                    display: 'none', // Chrome, Safari, Edge
                },
            } },
            React.createElement("div", { style: { marginTop: 'auto' } }, children)),
        React.createElement(Flex, { flxDirection: "column", sx: {
                display: isOpen ? 'flex' : 'none',
                padding: ['spaceS', 'spaceM'],
                paddingTop: ['spaceNone', 'spaceNone'],
                paddingBottom: ['spaceS', 'spaceM'],
                flexShrink: 0,
            } },
            React.createElement(Input, { ref: inputRef, lang: lang, disabled: inputDisabled, onSubmit: handleSendMessage, onChange: onInputChange, amaConversationID: amaConversationID, messageCount: messageCount }),
            React.createElement(Text, { fontSize: "fontSizeXxs", color: "colorTextSecondaryDefault", margin: "spaceS", marginBottom: "spaceNone", lineHeight: "lineHeightXxs" },
                chatStorageNotice
                    .split(chatStorageNoticeLinkText)
                    .map((part, i, parts) => i < parts.length - 1 ? (React.createElement(React.Fragment, { key: i },
                    part,
                    React.createElement("a", { href: chatStorageNoticeLinkUrl, target: "_blank", rel: "noopener noreferrer" }, chatStorageNoticeLinkText))) : (part)),
                ' ',
                React.createElement(TextLink, { href: reportProblemLinkUrl, variant: "secondary", isUnderlined: true }, reportProblemLinkText)))));
};
export default Sidebar;
